import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiLinkedin, FiArrowRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './About.css';

const About = () => {
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language || 'en';
  const [team, setTeam] = useState([]);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await axios.get(`/api/team?lang=${lang}`);
        setTeam(res.data.data || []);
      } catch (error) {
        console.error('Error fetching team:', error);
      }
    };
    fetchTeam();
  }, [lang]);

  const VALUES_I18N = {
    en: [
      { title: 'Excellence', description: 'We strive for excellence in everything we do, delivering exceptional results for our clients.' },
      { title: 'Innovation', description: 'We embrace new technologies and approaches to solve complex business challenges.' },
      { title: 'Integrity', description: 'We operate with honesty and transparency in all our client relationships.' },
      { title: 'Collaboration', description: 'We work together as one global team to achieve shared success.' }
    ],
    'zh-CN': [
      { title: '卓越', description: '我们在所做的每件事上追求卓越，为客户提供出色的成果。' },
      { title: '创新', description: '我们拥抱新技术和新方法，以解决复杂的商业挑战。' },
      { title: '诚信', description: '我们在所有客户关系中秉持诚实和透明的原则。' },
      { title: '协作', description: '我们作为一个全球团队共同努力，实现共同成功。' }
    ],
    'zh-TW': [
      { title: '卓越', description: '我們在所做的每件事上追求卓越，為客戶提供出色的成果。' },
      { title: '創新', description: '我們擁抱新技術和新方法，以解決複雜的商業挑戰。' },
      { title: '誠信', description: '我們在所有客戶關係中秉持誠實和透明的原則。' },
      { title: '協作', description: '我們作為一個全球團隊共同努力，實現共同成功。' }
    ],
    ja: [
      { title: '卓越性', description: 'すべての業務において卓越性を追求し、クライアントに卓越した成果を提供します。' },
      { title: '革新', description: '新しいテクノロジーとアプローチを積極的に採用し、複雑なビジネス課題を解決します。' },
      { title: '誠実性', description: 'すべてのクライアントとの関係において、誠実さと透明性をもって行動します。' },
      { title: 'コラボレーション', description: 'グローバルなチームとして協力し、共同の成功を実現します。' }
    ]
  };
  const values = VALUES_I18N[lang] || VALUES_I18N.en;

  const ABOUT_I18N = {
    en:      { heroLabel: 'About Us', heroTitle: 'Helping Global Brands Grow', heroDesc: 'With more than 650 brand launches over 20 years, we help global companies craft strategy and run operations across markets worldwide.', missionTitle: 'Our Mission', mission1: 'We exist to help ambitious brands navigate market complexity and achieve sustainable growth. Our teams combine deep local expertise with global perspective to deliver results that matter.', mission2: 'From market entry strategy to full-scale operations, we provide the infrastructure, intelligence, and execution capabilities brands need to succeed in competitive markets.', statsLabels: ['Brands Launched', 'Years Experience', 'Global Offices', 'Team Members'], valuesLabel: 'Our Values', valuesTitle: 'What Drives Us', teamLabel: 'Leadership', teamTitle: 'Meet Our Team', ctaTitle: 'Ready to Work Together?', ctaDesc: "Let's discuss how we can help your brand achieve its growth objectives.", ctaBtn: 'Get in Touch' },
    'zh-CN': { heroLabel: '关于我们', heroTitle: '助力全球品牌成长', heroDesc: '凭借20年逾650个品牌发布的经验，我们帮助全球企业制定战略并在全球市场运营。', missionTitle: '我们的使命', mission1: '我们的存在是为了帮助有抱负的品牌驾驭市场复杂性，实现可持续增长。我们的团队结合深厚的本地专业知识与全球视野，交付有意义的成果。', mission2: '从市场进入战略到全面运营，我们为品牌提供在竞争性市场中取得成功所需的基础设施、情报和执行能力。', statsLabels: ['品牌发布', '年经验', '全球办公室', '团队成员'], valuesLabel: '我们的价值观', valuesTitle: '驱动我们前行', teamLabel: '领导层', teamTitle: '认识我们的团队', ctaTitle: '准备好合作了吗？', ctaDesc: '让我们探讨如何帮助您的品牌实现增长目标。', ctaBtn: '立即联系' },
    'zh-TW': { heroLabel: '關於我們', heroTitle: '助力全球品牌成長', heroDesc: '憑借20年逾650個品牌發布的經驗，我們幫助全球企業制定戰略並在全球市場運營。', missionTitle: '我們的使命', mission1: '我們的存在是為了幫助有抱負的品牌駕馭市場複雜性，實現可持續增長。我們的團隊結合深厚的本地專業知識與全球視野，交付有意義的成果。', mission2: '從市場進入戰略到全面運營，我們為品牌提供在競爭性市場中取得成功所需的基礎設施、情報和執行能力。', statsLabels: ['品牌發布', '年經驗', '全球辦公室', '團隊成員'], valuesLabel: '我們的價值觀', valuesTitle: '驅動我們前行', teamLabel: '領導層', teamTitle: '認識我們的團隊', ctaTitle: '準備好合作了嗎？', ctaDesc: '讓我們探討如何幫助您的品牌實現增長目標。', ctaBtn: '立即聯繫' },
    ja:      { heroLabel: '会社概要', heroTitle: 'グローバルブランドの成長を支援', heroDesc: '20年以上にわたる650以上のブランドローンチの実績をもとに、世界中の市場で戦略立案と事業運営を支援します。', missionTitle: '私たちの使命', mission1: '私たちは、野心的なブランドが市場の複雑さを乗り越え、持続可能な成長を達成できるよう支援するために存在します。私たちのチームは、深いローカルの専門知識とグローバルな視点を組み合わせ、意味ある成果を提供します。', mission2: '市場参入戦略から全面的な事業運営まで、競争の激しい市場で成功するために必要なインフラ、インテリジェンス、実行能力をブランドに提供します。', statsLabels: ['ブランドローンチ', '年の経験', 'グローバルオフィス', 'チームメンバー'], valuesLabel: '私たちの価値観', valuesTitle: '私たちを動かすもの', teamLabel: 'リーダーシップ', teamTitle: 'チームをご紹介', ctaTitle: '一緒に働きませんか？', ctaDesc: 'ブランドの成長目標達成にどのようにお役立てできるかを話し合いましょう。', ctaBtn: 'お問い合わせ' }
  };
  const copy = ABOUT_I18N[lang] || ABOUT_I18N.en;

  return (
    <div className="about-page">
      <section className="page-hero">
        <div className="container">
          <p className="page-label">{copy.heroLabel}</p>
          <h1>{copy.heroTitle}</h1>
          <p className="page-description">{copy.heroDesc}</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="about-grid">
            <div className="about-content">
              <h2>{copy.missionTitle}</h2>
              <p>{copy.mission1}</p>
              <p>{copy.mission2}</p>
            </div>
            <div className="about-stats">
              {[['650+', 0], ['20+', 1], ['5', 2], ['12+', 3]].map(([num, idx]) => (
                <div key={idx} className="stat-box">
                  <span className="stat-number">{num}</span>
                  <span className="stat-text">{copy.statsLabels[idx]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <p className="section-label">{copy.valuesLabel}</p>
            <h2>{copy.valuesTitle}</h2>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <span className="value-number">0{index + 1}</span>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="team">
        <div className="container">
          <div className="section-header">
            <p className="section-label">{copy.teamLabel}</p>
            <h2>{copy.teamTitle}</h2>
          </div>
          <div className="team-grid">
            {team.map((member, index) => (
              <div key={member._id || index} className="team-card">
                <div className="team-image">
                  <div className="image-placeholder">
                    <span>{member.name.charAt(0)}</span>
                  </div>
                </div>
                <div className="team-info">
                  <h3>{member.name}</h3>
                  <p className="team-role">{member.role}</p>
                  <p className="team-bio">{member.bio}</p>
                  {member.linkedin && (
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="team-linkedin">
                      <FiLinkedin /> Connect
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-dark">
        <div className="container text-center">
          <h2>{copy.ctaTitle}</h2>
          <p style={{ maxWidth: '600px', margin: '0 auto 2rem' }}>{copy.ctaDesc}</p>
          <Link to="/contact" className="btn btn-primary">
            {copy.ctaBtn} <FiArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
